import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Platform } from '@ionic/angular';
import { AuthEventService } from './auth-event.service';
import { AuthConfigService } from '../utils/auth-config.service';
import { StorageService } from './storage.service';
import { GapSettings } from '../config/gapsettings';

/**
 * IdleService
 *
 * Simple timer-based idle service that logs out after inactivity timeout.
 * Resets timer on user activity and checks if user is authenticated before logout.
 */
@Injectable({
  providedIn: 'root'
})
export class IdleService implements OnDestroy {
  private inactivityTimer: any;
  private sessionTimer: any;
  private videoPlaying = false;
  private isInitialized = false;
  private lastActivityTime = 0;
  private activityThrottleDelay = 2000; // 2 seconds throttle

  constructor(
    private zone: NgZone,
    private authEventService: AuthEventService,
    private authConfig: AuthConfigService,
    private storage: StorageService,
    private platform: Platform,
    private gapSettings: GapSettings
  ) { }

  /**
   * Initialize idle monitoring - timer-based approach
   */
  public async init(): Promise<void> {
    if (!this.gapSettings.isSessionTimeoutEnabled()) {
      console.log('Session timeout is disabled');
      return;
    }
    // Prevent double initialization
    if (this.isInitialized) {
      console.log('IdleService: Already initialized, skipping');
      return;
    }

    console.log('IdleService: Initializing timer-based idle monitoring');
    
    this.lastActivityTime = Date.now();
    
    // Set up activity listeners
    this.setupActivityListeners();
    
    // Start inactivity and session timers
    this.startTimers();
    
    this.isInitialized = true;
    console.log('IdleService: Initialization complete');
  }

  /**
   * Set up activity listeners based on platform
   */
  private setupActivityListeners(): void {
    this.zone.runOutsideAngular(() => {
      let events: string[] = [];
      
      if (this.platform.is('mobile') || this.platform.is('android') || this.platform.is('ios')) {
        // Mobile devices: use touch events and common mobile interactions
        events = ['touchstart', 'touchmove', 'touchend', 'keydown', 'scroll', 'click'];
      } else {
        // Desktop/web: use mouse and keyboard events
        events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
      }
      
      console.log('IdleService: Setting up activity listeners for events:', events);
      
      events.forEach(evt => document.addEventListener(evt, this.throttledActivityListener, true));
    });
  }

  /**
   * Start timers for inactivity and session timeout
   */
  private startTimers(): void {
    console.log('IdleService: Starting inactivity and session timers');
    this.resetInactivityTimer();
    this.resetSessionTimer();
  }

  /**
   * Reset both timers when user activity is detected
   */
  private resetTimers(): void {
    this.resetInactivityTimer();
    this.resetSessionTimer();
  }

  /**
   * Reset inactivity timer
   */
  private resetInactivityTimer(): void {
    clearTimeout(this.inactivityTimer);
    
    const inactivityTimeout = this.authConfig.getInactivityTimeout();
    this.inactivityTimer = setTimeout(() => {
      this.handleInactivity();
    }, inactivityTimeout);
  }

  /**
   * Reset session timer
   */
  private resetSessionTimer(): void {
    clearTimeout(this.sessionTimer);
    
    const sessionTimeout = this.authConfig.getSessionTimeout();
    
    // Only set session timeout if it's greater than 0
    if (sessionTimeout > 0) {
      this.sessionTimer = setTimeout(() => {
        this.handleSessionTimeout();
      }, sessionTimeout);
    }
  }

  /**
   * Handle inactivity timeout - check if user is authenticated before logout
   */
  private async handleInactivity(): Promise<void> {
    // Check if user is authenticated before logging out
    const token = await this.storage.getTokenAsync();
    if (!token) {
      console.log('IdleService: No token found, stopping idle service');
      this.cleanup();
      return;
    }

    const currentMode = this.authConfig.getCurrentAuthMode();
    const timeoutSeconds = this.authConfig.getInactivityTimeout() / 1000;
    
    console.log(`IdleService: User inactive for ${timeoutSeconds} seconds in ${currentMode} mode, logging out...`);
    this.authEventService.emitInactivityLogout(`User inactive for ${timeoutSeconds} seconds (${currentMode} mode)`);
  }

  /**
   * Handle session timeout - check if user is authenticated before logout
   */
  private async handleSessionTimeout(): Promise<void> {
    // Check if user is authenticated before logging out
    const token = await this.storage.getTokenAsync();
    if (!token) {
      console.log('IdleService: No token found, stopping idle service');
      this.cleanup();
      return;
    }

    const currentMode = this.authConfig.getCurrentAuthMode();
    const timeoutHours = this.authConfig.getSessionTimeout() / (60 * 60 * 1000);
    
    console.log(`IdleService: Session exceeded ${timeoutHours} hours in ${currentMode} mode, logging out...`);
    this.authEventService.emitSessionExpired(`Session exceeded ${timeoutHours} hours (${currentMode} mode)`);
  }

  /**
   * Throttled activity listener to prevent excessive event handling
   */
  private throttledActivityListener = (): void => {
    const now = Date.now();
    
    // Skip if we're within the throttle delay (based on previous lastActivityTime)
    if (this.lastActivityTime > 0 && (now - this.lastActivityTime) < this.activityThrottleDelay) {
      return;
    }
    
    // Call activity listener which will update lastActivityTime
    this.activityListener();
  };

  /**
   * Handle user activity - reset timers and update activity time
   */
  private activityListener = (): void => {
    // Skip activity handling during video playback
    if (this.videoPlaying) {
      return;
    }

    // Update last activity time
    this.lastActivityTime = Date.now();
    
    // Reset timers on activity
    this.resetTimers();
  };

  /**
   * Validate session on app resume - check if timeout occurred while in background
   */
  public async validateSessionOnResume(): Promise<void> {
    if (this.isInitialized === false) {
      console.log('IdleService: Not initialized, skipping session validation');
      return;
    }
    // Check if user is authenticated
    const token = await this.storage.getTokenAsync();
    if (!token) {
      console.log('IdleService: No token found on resume, stopping idle service');
      this.cleanup();
      return;
    }

    // Check if inactivity timeout occurred while app was in background
    const now = Date.now();
    const timeSinceLastActivity = now - this.lastActivityTime;
    const inactivityTimeout = this.authConfig.getInactivityTimeout();

    console.log('IdleService: App resumed, checking for inactivity timeout:', {
      timeSinceLastActivity: timeSinceLastActivity / 1000,
      inactivityTimeout: inactivityTimeout / 1000,
      timeoutExceeded: timeSinceLastActivity > inactivityTimeout
    });

    // If inactivity timeout was exceeded while in background, log out immediately
    if (timeSinceLastActivity > inactivityTimeout) {
      const currentMode = this.authConfig.getCurrentAuthMode();
      const timeoutSeconds = inactivityTimeout / 1000;
      
      console.log(`IdleService: User was inactive for ${timeSinceLastActivity / 1000} seconds while app was in background (timeout: ${timeoutSeconds}s), logging out...`);
      this.authEventService.emitInactivityLogout(`User inactive for ${timeSinceLastActivity / 1000} seconds while app was in background (${currentMode} mode)`);
      return;
    }

    // If no timeout occurred, continue with existing timers (don't reset them)
    console.log('IdleService: App resumed, no inactivity timeout occurred, continuing with existing timers');
  }

  /**
   * Set video playing state
   */
  public setVideoPlaying(state: boolean): void {
    if (!this.gapSettings.isSessionTimeoutEnabled()) {
      console.log('Session timeout is disabled');
      return;
    }
    this.videoPlaying = state;

    if (state) {
      console.log('IdleService: Video started, pausing inactivity monitoring');
      clearTimeout(this.inactivityTimer);
      clearTimeout(this.sessionTimer);
    } else {
      console.log('IdleService: Video stopped, resuming inactivity monitoring');
      this.resetTimers();
    }
  }

  /**
   * Watch video - convenience method
   */
  public watchingVideo(): void {
    if (!this.gapSettings.isSessionTimeoutEnabled()) {
      console.log('Session timeout is disabled');
      return;
    }
    this.setVideoPlaying(true);
    
    // Reset video state after 10 minutes
    setTimeout(() => {
      this.setVideoPlaying(false);
      console.log('Video playback only allowed for 10 minutes');
    }, 10 * 60 * 1000);
  }

  /**
   * Reset the idle service state
   */
  public reset(): void {
    console.log('IdleService: Resetting service state');
    this.cleanup();
    this.isInitialized = false;
  }

  /**
   * Clean up idle service resources
   */
  public cleanup(): void {
    if (!this.isInitialized) {
      return;
    }

    console.log('IdleService: Cleaning up resources');
    
    // Clear timers
    clearTimeout(this.inactivityTimer);
    clearTimeout(this.sessionTimer);
    
    // Remove event listeners
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'touchmove', 'touchend', 'click'];
    events.forEach(evt => document.removeEventListener(evt, this.throttledActivityListener, true));
    
    this.isInitialized = false;
  }

  ngOnDestroy(): void {
    console.log('IdleService: Destroying service');
    this.cleanup();
  }
}